/**
 * Newsletter Annual Calendar — Feb 2026
 * ======================================
 *
 * A one-year editorial heatmap: 26 bi-weekly Tuesday slots for the
 * selected year, coloured by state so the founder can see the whole
 * cadence at a glance.
 *
 *   • Consumed (broadcast already fired)  — green
 *   • Queued (draft ready, cron will fire) — orange
 *   • Empty (nothing planned yet)          — grey dashed
 *   • Current + Next slot                  — purple ring accent
 *
 * Tiles are grouped by quarter (Q1 / Q2 / Q3 / Q4) to help visual
 * planning around seasonal campaigns and partner co-marketing windows.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { CalendarDays, ChevronLeft, ChevronRight, Loader2, CheckCircle2 } from 'lucide-react';
import { apiClient } from '../../api/client';

const BRAND_PURPLE = '#7B4FD8';
const BRAND_ORANGE = '#F5831F';
const BRAND_INK = '#1A1209';

const STATE_STYLES = {
  consumed: { bg: '#DCFCE7', fg: '#166534', border: '#86EFAC', label: 'Sent' },
  queued:   { bg: '#FFEDD5', fg: '#9A3412', border: '#FDBA74', label: 'Queued' },
  empty:    { bg: '#F9FAFB', fg: '#9CA3AF', border: '#E5E7EB', label: 'Empty' },
};

const quarterOf = (isoDate) => {
  const m = parseInt(isoDate.slice(5, 7), 10);
  return Math.min(4, Math.ceil(m / 3));
};

const fmtTuesday = (iso) => {
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', timeZone: 'UTC' });
};

const CalendarTile = ({ tile, onClick }) => {
  const s = STATE_STYLES[tile.state] || STATE_STYLES.empty;
  const isAccent = tile.is_current || tile.is_next;
  return (
    <button
      type="button"
      onClick={() => onClick && onClick(tile)}
      className="text-left rounded-md p-2 transition-all hover:shadow-sm"
      style={{
        backgroundColor: s.bg,
        color: s.fg,
        border: `${tile.state === 'empty' ? '1px dashed' : '1.5px solid'} ${s.border}`,
        boxShadow: isAccent ? `0 0 0 2px ${BRAND_PURPLE}55` : 'none',
        opacity: tile.is_past && tile.state === 'empty' ? 0.5 : 1,
      }}
      title={`${tile.slot} · Tue ${fmtTuesday(tile.tuesday)} · ${s.label}${tile.preview ? ` — ${tile.preview}` : ''}`}
      data-testid={`annual-cal-tile-${tile.slot}`}
      data-state={tile.state}
    >
      <div className="flex items-center justify-between gap-1 mb-0.5">
        <span className="text-[10px] font-bold">{tile.slot.replace(/^\d{4}-/, '')}</span>
        {tile.state === 'consumed' && <CheckCircle2 size={10} />}
      </div>
      <div className="text-[10px] leading-tight font-medium">{fmtTuesday(tile.tuesday)}</div>
      {tile.preview && (
        <div className="text-[9px] mt-0.5 line-clamp-2 opacity-80">{tile.preview}</div>
      )}
    </button>
  );
};

const NewsletterAnnualCalendar = ({ onSlotClick }) => {
  const nowYear = new Date().getUTCFullYear();
  const [year, setYear] = useState(nowYear);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const res = await apiClient.request(`/api/admin/newsletter/drafts/calendar?year=${year}`);
      setData(res);
    } catch (e) {
      setErr(e.message || 'Failed to load calendar');
    } finally {
      setLoading(false);
    }
  }, [year]);

  useEffect(() => { load(); }, [load]);

  const tiles = data?.tiles || [];
  const quarters = { 1: [], 2: [], 3: [], 4: [] };
  tiles.forEach((t) => { quarters[quarterOf(t.tuesday)].push(t); });

  const consumedCount = tiles.filter((t) => t.state === 'consumed').length;
  const queuedCount = tiles.filter((t) => t.state === 'queued').length;
  const emptyCount = tiles.filter((t) => t.state === 'empty').length;

  return (
    <div
      className="rounded-2xl border-2 shadow-sm bg-white"
      style={{ borderColor: '#EDE7F8' }}
      data-testid="newsletter-annual-calendar"
    >
      <div className="p-5 border-b flex items-center gap-3 flex-wrap" style={{ borderColor: '#F3F0FB' }}>
        <div className="rounded-xl p-2.5" style={{ backgroundColor: BRAND_PURPLE + '14', color: BRAND_PURPLE }}>
          <CalendarDays size={22} />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-base font-semibold" style={{ color: BRAND_INK }}>Annual Editorial Calendar — {year}</h3>
          <p className="text-sm text-gray-500 mt-0.5">
            26 bi-weekly Tuesday slots grouped by quarter. Sent · Queued · Empty at a glance.
          </p>
        </div>

        {/* Year selector */}
        <div className="inline-flex items-center gap-2" data-testid="annual-cal-year-selector">
          <button
            type="button"
            onClick={() => setYear(year - 1)}
            className="rounded p-1 hover:bg-gray-100"
            style={{ color: BRAND_PURPLE }}
            aria-label="Previous year"
            data-testid="annual-cal-year-prev"
          >
            <ChevronLeft size={16} />
          </button>
          <span className="text-sm font-bold min-w-[3ch] text-center" style={{ color: BRAND_INK }}>{year}</span>
          <button
            type="button"
            onClick={() => setYear(year + 1)}
            className="rounded p-1 hover:bg-gray-100"
            style={{ color: BRAND_PURPLE }}
            aria-label="Next year"
            data-testid="annual-cal-year-next"
          >
            <ChevronRight size={16} />
          </button>
          {year !== nowYear && (
            <button
              type="button"
              onClick={() => setYear(nowYear)}
              className="ml-1 text-[11px] font-semibold px-2 py-0.5 rounded-full"
              style={{ backgroundColor: BRAND_PURPLE + '14', color: BRAND_PURPLE }}
              data-testid="annual-cal-year-today"
            >
              Today
            </button>
          )}
        </div>
      </div>

      {/* Summary strip */}
      <div className="px-5 pt-3 pb-2 flex items-center gap-3 flex-wrap text-[11px]">
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full" style={{ backgroundColor: STATE_STYLES.consumed.bg, color: STATE_STYLES.consumed.fg }} data-testid="annual-cal-count-consumed">
          <span className="w-2 h-2 rounded-full" style={{ backgroundColor: STATE_STYLES.consumed.fg }} />
          Sent <b>{consumedCount}</b>
        </span>
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full" style={{ backgroundColor: STATE_STYLES.queued.bg, color: STATE_STYLES.queued.fg }} data-testid="annual-cal-count-queued">
          <span className="w-2 h-2 rounded-full" style={{ backgroundColor: STATE_STYLES.queued.fg }} />
          Queued <b>{queuedCount}</b>
        </span>
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full" style={{ backgroundColor: STATE_STYLES.empty.bg, color: STATE_STYLES.empty.fg }} data-testid="annual-cal-count-empty">
          <span className="w-2 h-2 rounded-full" style={{ backgroundColor: STATE_STYLES.empty.fg }} />
          Empty <b>{emptyCount}</b>
        </span>
        {data?.current_slot && (
          <span className="ml-auto text-[11px] font-semibold" style={{ color: BRAND_PURPLE }}>
            ● Current {data.current_slot}
          </span>
        )}
      </div>

      {err && (
        <div className="px-5 py-2 text-sm" style={{ color: '#991B1B' }}>{err}</div>
      )}

      {/* Quarters */}
      <div className="p-5 pt-3 space-y-4">
        {loading && !data ? (
          <div className="flex items-center justify-center py-6 text-gray-400">
            <Loader2 size={18} className="animate-spin mr-2" /> Loading calendar…
          </div>
        ) : (
          [1, 2, 3, 4].map((q) => (
            <div key={q} data-testid={`annual-cal-quarter-${q}`}>
              <div className="text-[10px] font-bold uppercase tracking-widest mb-1.5" style={{ color: '#6B7280' }}>
                Q{q}
              </div>
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-7 gap-1.5">
                {quarters[q].map((tile) => (
                  <CalendarTile
                    key={tile.slot}
                    tile={tile}
                    onClick={onSlotClick}
                  />
                ))}
                {quarters[q].length === 0 && (
                  <div className="col-span-full text-[11px] italic text-gray-400">No slots this quarter</div>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default NewsletterAnnualCalendar;
